import { damageDetailsDefaultFormValues } from '../DamageDetails';
import { FormFields, FormHeader, FormSection } from '../form';
import { LocateMarkings } from './LocateMarkings';
import { useValidation } from '../../../../hooks';
import { ListBox } from '../../../../components';
import { useFormContext } from 'react-hook-form';
import { claimFields } from '../../../../config';
import { useEffect, useMemo } from 'react';

import type { IClaim, IClaimInit, IListItem } from '../../../../types';
import type { Dispatch, FC, SetStateAction } from 'react';
import { useContext } from 'react';
import { Localization } from '../../../../config/localization/localization';
import { LanguageSelectorContext } from '../../../../context/LanguageSelectorContext';

interface ILocatesProps {
  claimInit?: IClaimInit | IClaim | null;
  setSaveAndQuitChanges?: Dispatch<SetStateAction<Array<string>>>;
}

export const Locates: FC<ILocatesProps> = ({ claimInit, setSaveAndQuitChanges }) => {
  const formContext = useFormContext();
  const { fieldRequirements } = useValidation({});
  const { selectedLanguage } = useContext(LanguageSelectorContext);
  const localization = new Localization(selectedLanguage);

  const showLocateAccuracy: boolean = formContext.getValues(claimFields.wereLocatesPresent) === '1';

  const wereLocatesPresentOptions: Array<IListItem> = [
    { id: '0', name: localization.translate('dropdownOptions.selectOption') },
    { id: '1', name: localization.translate('dropdownOptions.yes') },
    { id: '2', name: localization.translate('dropdownOptions.no') },
    { id: '3', name: localization.translate('dropdownOptions.na') },
    { id: '4', name: localization.translate('dropdownOptions.unknown') },
  ];

  const locateAccuracies = useMemo(
    () =>
      claimInit?.locateAccuracyList
        ?.filter((a) => a.active && a.isSelectableInMrs)
        .map((a) => {
          return { id: a.id, name: localization.getFromListByKey('locateAccuracies', a.id, a.label) } as IListItem;
        })
        .sort((a, b) => a.name.toUpperCase().localeCompare(b.name.toUpperCase())) ?? [],
    [claimInit?.locateAccuracyList, localization]
  );

  useEffect(() => {
    if (claimInit && 'locateAccuracyId' in claimInit) {
      const accuracy = claimInit.locateAccuracyList.find((a) => Number(a.id) === claimInit.locateAccuracyId);
      const alreadyExists =
        locateAccuracies.find((a) => Number(a.id) === claimInit.locateAccuracyId) !== undefined;
      if (accuracy && !alreadyExists)
        locateAccuracies.push({ id: accuracy.id, name: accuracy.label } as IListItem);
    }
  }, [claimInit, locateAccuracies]);

  const onLocateAccuracyChange = () => {
    setSaveAndQuitChanges?.((prev) => [...prev, 'LocateAccuracyId']);
    formContext.clearErrors(claimFields.locateAccuracy);
  };

  const onWereLocatesPresentChange = (item: IListItem) => {
    setSaveAndQuitChanges?.((prev) => [
      ...prev,
      claimFields.wereLocatesPresent,
      claimFields.wereLocatesPresentNA,
      claimFields.wereLocatesPresentUnknown,
    ]);

    if (item.id === '2' || item.id === '3' || item.id === '4') {
      formContext.setValue(
        claimFields.locateAccuracy,
        damageDetailsDefaultFormValues[claimFields.locateAccuracy]
      );
      formContext.setValue(claimFields.locateMarkingFlags, false);
      formContext.setValue(claimFields.locateMarkingPaint, false);
      formContext.setValue(claimFields.locateMarkingStakes, false);
      formContext.setValue('isMarkingUnknown', false);
      formContext.clearErrors(claimFields.locateAccuracy);
      formContext.clearErrors('isMarkingUnknown');
      formContext.clearErrors(claimFields.locateMarkingFlags);
      formContext.clearErrors(claimFields.locateMarkingPaint);
      formContext.clearErrors(claimFields.locateMarkingStakes);
    }

    formContext.setValue(claimFields.wereLocatesPresent, item.id);
    formContext.setValue(claimFields.wereLocatesPresentNA, item.id === '3' ? true : null);
    formContext.setValue(claimFields.wereLocatesPresentUnknown, item.id === '4' ? true : null);
    formContext.clearErrors(claimFields.wereLocatesPresent);
  };

  return (
    <FormSection>
      <FormHeader>{localization.translate('locates.header')}</FormHeader>

      <FormFields>
        {/* WERE LOCATES PRESENT? */}
        <ListBox
          isRequired={fieldRequirements.WereLocatesPresent}
          label={localization.translate('locates.wereLocatesPresentLabel')}
          listItems={wereLocatesPresentOptions}
          name={claimFields.wereLocatesPresent}
          onSingleChange={onWereLocatesPresentChange}
          tooltip={localization.translate('locates.wereLocatesPresentTooltip')}
        />

        {showLocateAccuracy && (
          <>
            {/* LOCATE ACCURACY */}
            <ListBox
              isRequired={fieldRequirements.LocateAccuracy}
              label={localization.translate('locates.locateAccuracyLabel')}
              listItems={locateAccuracies}
              name={claimFields.locateAccuracy}
              onSingleChange={onLocateAccuracyChange}
              tooltip={localization.translate('locates.locateAccuracyTooltip')}
            />

            {/* LOCATE MARKINGS */}
            <LocateMarkings setSaveAndQuitChanges={setSaveAndQuitChanges} />
          </>
        )}
      </FormFields>
    </FormSection>
  );
};